<!-- src/views/pages/finance-accounts/sales/invoice-details.vue -->
<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
import { useInvoiceStore } from "@/stores/invoiceStore";
import { useCompanyStore } from "@/stores/companyStore";
import taxComplianceService from "@/services/finance/taxCompliance.service";
import { toast } from "@/utils/toast";

const route = useRoute();
const router = useRouter();

const invoiceStore = useInvoiceStore();
const companyStore = useCompanyStore();

const { current } = storeToRefs(invoiceStore);
const { activeCompanyId, companies } = storeToRefs(companyStore);

const loading = ref(false);
const complianceLoading = ref(false);
const complianceSubmitting = ref(false);
const complianceRecord = ref(null);

// Page breadcrumb labels
const title = ref("Invoices");
const text = ref("Finance & Accounts");
const text1 = ref("Invoices");

// ====== COMPANY (From) ======
const activeCompany = computed(() => {
  return companies.value?.find((c) => c.id === activeCompanyId.value) || null;
});

const companyLogo = computed(() => {
  return (
    activeCompany.value?.logo_url ||
    activeCompany.value?.logo ||
    activeCompany.value?.company_logo ||
    null
  );
});

const companyInitials = computed(() => {
  const name = activeCompany.value?.company_name || "";
  if (!name) return "CN";
  return name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
});

// ====== INVOICE DATA ======
const invoice = computed(() => current.value || null);
const complianceInvoice = computed(() => complianceRecord.value || invoice.value || null);

const items = computed(() => invoice.value?.items || []);
const linkedAdjustments = computed(() => invoice.value?.child_adjustments || []);

const payments = computed(() => invoice.value?.payments || []);

const amountPaid = computed(() => Number(invoice.value?.amount_paid || 0));

const amountDue = computed(() => Number(invoice.value?.amount_due || 0));
const creditedTotal = computed(() => Number(invoice.value?.credited_total || 0));
const debitAdjustmentTotal = computed(() => Number(invoice.value?.debit_adjustment_total || 0));
const complianceLogs = computed(() => complianceInvoice.value?.compliance_logs || []);

// ====== HELPERS ======
function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function isFullyPaid() {
  return amountDue.value <= 0 || invoice.value.status === "paid";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatDocumentType(type) {
  const normalized = String(type || "").toLowerCase();
  if (!normalized) return "Invoice";
  if (normalized === "credit_note") return "Credit Note";
  if (normalized === "debit_note") return "Debit Note";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function timelineTone(type) {
  if (type === "payment") return "success";
  if (type === "credit_note") return "warning";
  if (type === "debit_note") return "info";
  if (type === "compliance_error") return "danger";
  if (type === "compliance") return "primary";
  return "secondary";
}

function complianceBadge(status) {
  switch ((status || "").toLowerCase()) {
    case "accepted":
      return { class: "badge-soft-success", label: "Accepted" };
    case "submitted":
      return { class: "badge-soft-info", label: "Submitted" };
    case "queued":
      return { class: "badge-soft-warning", label: "Queued" };
    case "rejected":
      return { class: "badge-soft-danger", label: "Rejected" };
    default:
      return { class: "badge-soft-secondary", label: "Not Submitted" };
  }
}

// function daysBetween(from, to) {
//   const a = new Date(from);
//   const b = new Date(to);
//   if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
//   const diffMs = b.getTime() - a.getTime();
//   return Math.round(diffMs / (1000 * 60 * 60 * 24));
// }

// ====== TOTALS ======
const subTotal = computed(() =>
  items.value.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const rate = Number(item.rate) || 0;
    return sum + qty * rate;
  }, 0)
);

const discountTotal = computed(() =>
  items.value.reduce((sum, item) => sum + (Number(item.discount) || 0), 0)
);

// If you later add tax_rate to invoice, this will pick it up as percentage
const taxRate = computed(() => Number(invoice.value?.tax_rate || 0));

const taxTotal = computed(() => {
  const taxable = subTotal.value - discountTotal.value;
  if (!taxRate.value) return 0;
  return (taxable * taxRate.value) / 100;
});

const grandTotal = computed(
  () => subTotal.value - discountTotal.value + taxTotal.value
);
const effectiveTotal = computed(() =>
  Number(
    invoice.value?.effective_total != null
      ? invoice.value.effective_total
      : grandTotal.value
  )
);

const projectLabel = computed(() => {
  return invoice.value?.project?.name || invoice.value?.project?.project_name || "";
});

const invoiceContactEmail = computed(() => {
  return invoice.value?.email || invoice.value?.client?.email || "";
});

const invoiceClientAddress = computed(() => {
  return invoice.value?.client_address || invoice.value?.client?.address || "";
});

const invoiceBillingAddress = computed(() => {
  return invoice.value?.billing_address || invoiceClientAddress.value;
});
const documentTypeLabel = computed(() => {
  const type = String(invoice.value?.adjustment_type || "").toLowerCase();
  if (type === "credit_note") return "Credit Note";
  if (type === "debit_note") return "Debit Note";
  return "Invoice";
});
const activityTimeline = computed(() => {
  const rows = [];

  if (invoice.value?.created_at) {
    rows.push({
      id: `created-${invoice.value.id}`,
      type: "created",
      title: `${documentTypeLabel.value} created`,
      description: `${invoice.value.invoice_no || "Document"} was created.`,
      happened_at: invoice.value.created_at,
      amount: Number(invoice.value.total || 0),
    });
  }

  if (invoice.value?.parent_invoice_id) {
    rows.push({
      id: `source-${invoice.value.id}`,
      type: "source_link",
      title: `${documentTypeLabel.value} linked`,
      description: `Linked to source invoice ${invoice.value.parent_invoice?.invoice_no || `#${invoice.value.parent_invoice_id}`}.`,
      happened_at: invoice.value.created_at || invoice.value.invoice_date,
    });
  }

  for (const payment of payments.value) {
    rows.push({
      id: `payment-${payment.id}`,
      type: "payment",
      title: "Payment received",
      description: `${payment.method || "Payment"}${payment.reference ? ` • ${payment.reference}` : ""}`,
      happened_at: payment.payment_date || payment.paid_at || payment.created_at,
      amount: Number(payment.amount || 0),
    });
  }

  for (const adjustment of linkedAdjustments.value) {
    rows.push({
      id: `adjustment-${adjustment.id}`,
      type: String(adjustment.adjustment_type || "").toLowerCase() || "adjustment",
      title: `${formatDocumentType(adjustment.adjustment_type)} issued`,
      description: adjustment.invoice_no || `Adjustment #${adjustment.id}`,
      happened_at: adjustment.invoice_date || adjustment.created_at,
      amount: Number(adjustment.total || 0),
      route: { name: "sales.invoices.show", params: { id: adjustment.id } },
    });
  }

  for (const log of complianceLogs.value) {
    const isError = Boolean(log.error_message) || ["failed", "rejected"].includes(String(log.status || "").toLowerCase());
    rows.push({
      id: `compliance-${log.id}`,
      type: isError ? "compliance_error" : "compliance",
      title: `eTIMS ${String(log.action || "activity").replace("_", " ")}`,
      description: isError
        ? log.error_message
        : `${String(log.status || "processed")} via ${String(log.provider || "etims")}`,
      happened_at: log.attempted_at || log.created_at,
    });
  }

  return rows
    .filter((row) => row.happened_at)
    .sort((a, b) => new Date(b.happened_at).getTime() - new Date(a.happened_at).getTime());
});

// ====== PAYMENT STATUS BADGE / TEXT ======
const paymentStatusBadge = computed(() => {
  const status = (invoice.value?.status || "").toLowerCase();
  switch (status) {
    case "paid":
      return { class: "badge-soft-success", label: "Paid" };
    case "pending":
      return { class: "badge-soft-warning", label: "Pending" };
    case "overdue":
      return { class: "badge-soft-danger", label: "Overdue" };
    case "draft":
      return { class: "badge-soft-secondary", label: "Draft" };
    default:
      return { class: "badge-soft-secondary", label: "Unknown" };
  }
});

// const paymentStatusSubText = computed(() => {
//   const due = invoice.value?.due_date;
//   if (!due) return "";
//   const today = new Date();
//   const diff = daysBetween(today, due); // positive => due in X days
//   if (diff === null) return "";

//   if (diff > 0) {
//     return `Due in ${diff} day${diff === 1 ? "" : "s"}`;
//   } else if (diff < 0) {
//     const overdueDays = Math.abs(diff);
//     return `Overdue by ${overdueDays} day${overdueDays === 1 ? "" : "s"}`;
//   }
//   return "Due today";
// });

// ====== ACTIONS ======

function normalizeDate(val) {
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0); // strip time so we only compare dates
  return d;
}

const paymentStatusSubText = computed(() => {
  const status = (invoice.value?.status || "").toLowerCase();
  const adjustmentType = (invoice.value?.adjustment_type || "").toLowerCase();

  if (adjustmentType === "credit_note") {
    return "This credit note is recorded as a settled adjustment.";
  }

  // For PAID invoices, don't show overdue/remaining days
  if (status === "paid") {
    return "Payment received in full";
  }

  const dueRaw = invoice.value?.due_date;
  if (!dueRaw) return "";

  const dueDate = normalizeDate(dueRaw);
  const today = normalizeDate(new Date());

  if (!dueDate || !today) return "";

  const diffDays =
    (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

  if (diffDays > 0) {
    const d = Math.round(diffDays);
    return `Due in ${d} day${d === 1 ? "" : "s"}`;
  } else if (diffDays < 0) {
    const d = Math.abs(Math.round(diffDays));
    return `Overdue by ${d} day${d === 1 ? "" : "s"}`;
  }

  return "Due today";
});

async function loadInvoice() {
  const id = route.params.id || route.query.id;
  if (!id) {
    router.push("/sales/invoices");
    return;
  }

  try {
    loading.value = true;
    await invoiceStore.get(id); // sets invoiceStore.current
  } catch (e) {
    toast.error("Failed to load invoice");
    router.push("/sales/invoices");
  } finally {
    loading.value = false;
  }
}

async function loadCompliance() {
  const id = route.params.id || route.query.id;
  if (!id) return;

  try {
    complianceLoading.value = true;
    const res = await taxComplianceService.getInvoiceCompliance(id);
    complianceRecord.value = res.data || null;
  } catch (e) {
    console.warn("Failed to load invoice compliance", e);
  } finally {
    complianceLoading.value = false;
  }
}

async function submitEtims(mode = "submit") {
  if (!invoice.value?.id) return;

  try {
    complianceSubmitting.value = true;
    if (mode === "resubmit") {
      await taxComplianceService.resubmitEtims(invoice.value.id);
      toast.success("Invoice requeued for eTIMS submission");
    } else {
      await taxComplianceService.submitEtims(invoice.value.id);
      toast.success("Invoice queued for eTIMS submission");
    }
    await invoiceStore.get(invoice.value.id);
    await loadCompliance();
  } catch (e) {
    toast.error(e?.response?.data?.message || "Failed to queue invoice for eTIMS");
  } finally {
    complianceSubmitting.value = false;
  }
}

function printInvoice() {
  window.print();
}

function cloneInvoice() {
  // Simple approach: navigate to add-invoices with the ID to be cloned in query.
  // You can handle the cloning logic in add-invoices.vue if you want to prefill.
  if (!invoice.value?.id) return;
  router.push({
    path: "/sales/invoices/add-invoices",
    query: { clone_id: invoice.value.id },
  });
}

function issueCreditNote() {
  if (!invoice.value?.id) return;
  router.push({
    path: "/sales/invoices/add-invoices",
    query: {
      clone_id: invoice.value.id,
      parent_invoice_id: invoice.value.id,
      adjustment_type: "credit_note",
    },
  });
}

// ====== LIFECYCLE ======
onMounted(async () => {
  if (!companyStore?.companies?.length) {
    await companyStore.fetchCompanies();
  }
  await loadInvoice();
  await loadCompliance();
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <button
              type="button"
              class="btn btn-dark d-flex align-items-center"
              @click="printInvoice"
            >
              <i class="ti ti-download me-2"></i>Download / Print
            </button>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Invoices -->
      <div v-if="invoice">
        <div class="row dq-invoice-shell" :class="{ 'opacity-50': loading }">
          <div class="col-sm-10 mx-auto">
            <router-link
              to="/sales/invoices"
              class="back-icon d-flex align-items-center fs-12 fw-medium mb-3 d-inline-flex"
            >
              <span
                class="d-flex justify-content-center align-items-center rounded-circle me-2"
              >
                <i class="ti ti-arrow-left"></i>
              </span>
              Back to List
            </router-link>
            <div class="card">
              <div class="card-body">
                <!-- Header -->
                <div
                  class="row justify-content-between align-items-center border-bottom mb-3 dq-invoice-header"
                >
                  <div class="col-md-6">
                    <div class="mb-2">
                      <template v-if="companyLogo">
                        <img :src="companyLogo" class="img-fluid" alt="logo" />
                      </template>
                      <template v-else>
                        <div
                          class="avatar avatar-lg bg-primary text-white d-inline-flex align-items-center justify-content-center rounded"
                        >
                          <span class="fw-bold">{{ companyInitials }}</span>
                        </div>
                      </template>
                    </div>

                    <p>
                      {{
                        activeCompany?.address ||
                        "Company address, city, country"
                      }}
                    </p>
                  </div>
                  <div class="col-md-6">
                    <div class="text-end mb-3">
                      <h5 class="text-gray mb-1">
                        {{ documentTypeLabel }} No
                        <span class="text-primary">
                          {{ invoice.invoice_no || "—" }}
                        </span>
                      </h5>
                      <p v-if="invoice.parent_invoice_id" class="mb-1 fw-medium">
                        Source Invoice :
                        <router-link
                          :to="{ name: 'sales.invoices.show', params: { id: invoice.parent_invoice_id } }"
                          class="text-primary"
                        >
                          {{ invoice.parent_invoice?.invoice_no || `#${invoice.parent_invoice_id}` }}
                        </router-link>
                      </p>
                      <p class="mb-1 fw-medium">
                        Created Date :
                        <span class="text-dark">
                          {{ formatDate(invoice.invoice_date) }}
                        </span>
                      </p>
                      <p class="fw-medium">
                        Due Date :
                        <span class="text-dark">
                          {{ formatDate(invoice.due_date) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- From / To / Status -->
                <div class="row border-bottom mb-3 dq-invoice-parties">
                  <!-- From -->
                  <div class="col-md-5">
                    <p class="text-dark mb-2 fw-semibold">From</p>
                    <div>
                      <h4 class="mb-1">
                        {{ activeCompany?.company_name || "Company Name" }}
                      </h4>
                      <p class="mb-1">
                        {{
                          activeCompany?.address ||
                          "Company address, city, country"
                        }}
                      </p>
                      <p class="mb-1">
                        Email :
                        <span class="text-dark">
                          {{ activeCompany?.email || "company@example.com" }}
                        </span>
                      </p>
                      <p>
                        Phone :
                        <span class="text-dark">
                          {{ activeCompany?.phone || "+000 000 000" }}
                        </span>
                      </p>
                    </div>
                  </div>

                  <!-- To -->
                  <div class="col-md-5">
                    <p class="text-dark mb-2 fw-semibold">To</p>
                    <div>
                      <h4 class="mb-1">
                        {{ invoice.client?.name || "Client Name" }}
                      </h4>
                      <p class="mb-1">
                        {{ invoiceClientAddress || "Client address, city, country" }}
                      </p>
                      <p class="mb-1">
                        Email :
                        <span class="text-dark">
                          {{ invoiceContactEmail || "client@example.com" }}
                        </span>
                      </p>
                      <p>
                        Phone :
                        <span class="text-dark">
                          {{ invoice.client?.phone || "+000 000 000" }}
                        </span>
                      </p>
                      <p v-if="invoiceBillingAddress" class="mb-1">
                        Billing Address :
                        <span class="text-dark">
                          {{ invoiceBillingAddress }}
                        </span>
                      </p>
                      <p v-if="projectLabel" class="mb-0">
                        Project :
                        <span class="text-dark">
                          {{ projectLabel }}
                        </span>
                      </p>
                    </div>
                  </div>

                  <!-- Status -->
                  <div class="col-md-2 dq-invoice-status-col">
                    <div class="mb-3">
                      <p class="text-title mb-2 fw-medium">Payment Status</p>
                      <span
                        class="badge align-items-center mb-3"
                        :class="paymentStatusBadge.class"
                      >
                        <i class="ti ti-point-filled me-1"></i>
                        {{ paymentStatusBadge.label }}
                      </span>
                      <p class="small text-muted mb-3">
                        {{ paymentStatusSubText }}
                      </p>
                      <div>
                        <img
                          src="@/assets/img/qr.svg"
                          class="img-fluid"
                          alt="QR"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row border-bottom mb-3">
                  <div class="col-md-8">
                    <div class="dq-compliance-card">
                      <div class="d-flex flex-wrap justify-content-between gap-3 align-items-start">
                        <div>
                          <div class="dq-compliance-eyebrow">Kenya eTIMS</div>
                          <h6 class="mb-1">Invoice Compliance</h6>
                          <p class="text-muted mb-0">
                            Queue this invoice for Kenya tax submission and review its compliance trail.
                          </p>
                        </div>
                        <span
                          class="badge align-items-center"
                          :class="complianceBadge(complianceInvoice?.etims_submission_status || complianceInvoice?.compliance_status).class"
                        >
                          <i class="ti ti-point-filled me-1"></i>
                          {{ complianceBadge(complianceInvoice?.etims_submission_status || complianceInvoice?.compliance_status).label }}
                        </span>
                      </div>

                      <div class="row g-3 mt-1">
                        <div class="col-md-3 col-6">
                          <div class="dq-meta-label">Compliance Status</div>
                          <div class="dq-meta-value">{{ complianceInvoice?.compliance_status || "not_submitted" }}</div>
                        </div>
                        <div class="col-md-3 col-6">
                          <div class="dq-meta-label">Submission Status</div>
                          <div class="dq-meta-value">{{ complianceInvoice?.etims_submission_status || "not_submitted" }}</div>
                        </div>
                        <div class="col-md-3 col-6">
                          <div class="dq-meta-label">Reference No</div>
                          <div class="dq-meta-value">{{ complianceInvoice?.etims_reference_no || "—" }}</div>
                        </div>
                        <div class="col-md-3 col-6">
                          <div class="dq-meta-label">Control Code</div>
                          <div class="dq-meta-value">{{ complianceInvoice?.etims_control_code || "—" }}</div>
                        </div>
                      </div>

                      <div
                        v-if="complianceInvoice?.etims_last_error"
                        class="alert alert-danger mt-3 mb-0"
                      >
                        <strong>Last error:</strong>
                        {{ complianceInvoice.etims_last_error }}
                      </div>

                      <div class="d-flex flex-wrap gap-2 mt-3">
                        <button
                          type="button"
                          class="btn btn-primary"
                          v-can="'Invoice.Edit'"
                          @click="submitEtims('submit')"
                          :disabled="complianceSubmitting || complianceLoading"
                        >
                          <span v-if="complianceSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                          Submit to eTIMS
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          v-can="'Invoice.Edit'"
                          @click="submitEtims('resubmit')"
                          :disabled="complianceSubmitting || complianceLoading"
                        >
                          Resubmit
                        </button>
                        <router-link
                          to="/financial-settings/kenya-tax-compliance"
                          class="btn btn-outline-secondary"
                        >
                          Kenya Tax Settings
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="dq-log-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Compliance Log</h6>
                        <span class="text-muted small">{{ complianceLogs.length }} entries</span>
                      </div>

                      <div v-if="complianceLoading" class="text-muted small">Loading compliance log...</div>
                      <div v-else-if="!complianceLogs.length" class="text-muted small">
                        No compliance activity yet.
                      </div>
                      <div v-else class="dq-log-list">
                        <div v-for="log in complianceLogs" :key="log.id" class="dq-log-item">
                          <div class="d-flex justify-content-between gap-2">
                            <strong class="text-capitalize">{{ log.action }}</strong>
                            <span class="text-muted small">{{ formatDate(log.attempted_at || log.created_at) }}</span>
                          </div>
                          <div class="small text-muted text-capitalize">
                            {{ log.status }} · {{ log.provider }}
                          </div>
                          <div v-if="log.error_message" class="small text-danger mt-1">
                            {{ log.error_message }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Items -->
                <div>
                  <p class="fw-medium">
                    Invoice For :
                    <span class="text-dark fw-medium">
                      {{ invoice.title || "Invoice Title" }}
                    </span>
                  </p>
                  <div class="table-responsive mb-3">
                    <table class="table">
                      <thead class="thead-light">
                        <tr>
                          <th>Job Description</th>
                          <th class="text-end">Qty</th>
                          <th class="text-end">Cost</th>
                          <th class="text-end">Discount</th>
                          <th class="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in items" :key="idx">
                          <td>
                            <h6>{{ item.description }}</h6>
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ item.qty }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ formatMoney(item.rate) }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ formatMoney(item.discount) }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{
                              formatMoney(
                                item.total ??
                                  item.qty * item.rate - item.discount
                              )
                            }}
                          </td>
                        </tr>
                        <tr v-if="!items.length">
                          <td colspan="5" class="text-center text-muted">
                            No items added for this invoice.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Terms / Totals -->
                <div class="row border-bottom mb-3">
                  <div v-if="linkedAdjustments.length" class="col-12 mb-3">
                    <div class="dq-log-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Linked Adjustments</h6>
                        <span class="text-muted small">{{ linkedAdjustments.length }} linked</span>
                      </div>
                      <div class="table-responsive">
                        <table class="table table-sm mb-0">
                          <thead class="thead-light">
                            <tr>
                              <th>Document</th>
                              <th>Type</th>
                              <th>Date</th>
                              <th class="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="adjustment in linkedAdjustments" :key="adjustment.id">
                              <td>
                                <router-link
                                  :to="{ name: 'sales.invoices.show', params: { id: adjustment.id } }"
                                  class="text-primary fw-medium"
                                >
                                  {{ adjustment.invoice_no }}
                                </router-link>
                              </td>
                              <td>{{ formatDocumentType(adjustment.adjustment_type) }}</td>
                              <td>{{ formatDate(adjustment.invoice_date) }}</td>
                              <td class="text-end">{{ formatMoney(adjustment.total) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div v-if="activityTimeline.length" class="col-12 mb-3">
                    <div class="dq-log-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Activity Timeline</h6>
                        <span class="text-muted small">{{ activityTimeline.length }} events</span>
                      </div>
                      <div class="dq-log-list">
                        <div v-for="event in activityTimeline" :key="event.id" class="dq-log-item">
                          <div class="d-flex justify-content-between align-items-start gap-3">
                            <div>
                              <div class="d-flex align-items-center gap-2 flex-wrap">
                                <span class="badge" :class="`badge-soft-${timelineTone(event.type)}`">
                                  {{ event.title }}
                                </span>
                                <router-link
                                  v-if="event.route"
                                  :to="event.route"
                                  class="small text-primary"
                                >
                                  Open
                                </router-link>
                              </div>
                              <div class="small text-muted mt-1">
                                {{ event.description }}
                              </div>
                            </div>
                            <div class="text-end">
                              <div v-if="event.amount != null" class="fw-medium">
                                {{ formatMoney(event.amount) }}
                              </div>
                              <div class="small text-muted">
                                {{ formatDateTime(event.happened_at) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-7">
                    <div class="py-4">
                      <div class="mb-3">
                        <h6 class="mb-1">Terms and Conditions</h6>
                        <p>
                          {{
                            invoice.terms ||
                            "Please pay within the agreed payment period. Overdue invoices may incur interest."
                          }}
                        </p>
                      </div>
                      <div class="mb-3">
                        <h6 class="mb-1">Notes</h6>
                        <p>
                          {{
                            invoice.notes ||
                            "Please quote invoice number when remitting funds."
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-5">
                    <div
                      class="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3"
                    >
                      <p class="mb-0">Sub Total</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(subTotal) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3"
                    >
                      <p class="mb-0">
                        Discount
                        <span v-if="taxRate">(&nbsp;)</span>
                      </p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(discountTotal) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <p class="mb-0">
                        VAT
                        <span v-if="taxRate">({{ taxRate }}%)</span>
                      </p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(taxTotal) }}
                      </p>
                    </div>
                    <div
                      v-if="creditedTotal > 0"
                      class="d-flex justify-content-between align-items-center border-top pt-2 mt-2 pe-3"
                    >
                      <p class="mb-0">Credited Total</p>
                      <p class="text-dark fw-medium mb-2">
                        - {{ formatMoney(creditedTotal) }}
                      </p>
                    </div>
                    <div
                      v-if="debitAdjustmentTotal > 0"
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <p class="mb-0">Debit Adjustments</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(debitAdjustmentTotal) }}
                      </p>
                    </div>
                    <div
                      v-if="creditedTotal > 0 || debitAdjustmentTotal > 0"
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <p class="mb-0">Effective Invoice Total</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(effectiveTotal) }}
                      </p>
                    </div>

                    <div
                      class="d-flex justify-content-between align-items-center border-top pt-2 mt-2 pe-3"
                    >
                      <p class="mb-0">Amount Paid</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(amountPaid) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <p class="mb-0">Balance Due</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(amountDue) }}
                      </p>
                    </div>

                    <div
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <h5>Total Amount</h5>
                      <h5>{{ formatMoney(grandTotal) }}</h5>
                    </div>
                    <!-- <p class="fs-12">
                      Amount in Words :
                      <span class="text-dark">
                        {{ formatMoney(grandTotal) }} only
                      </span>
                    </p> -->
                  </div>
                </div>

                <!-- Payment History -->
                <div class="row border-bottom mb-3" v-if="payments.length">
                  <div class="col-md-12">
                    <h6 class="mb-2">Payment History</h6>
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="thead-light">
                          <tr>
                            <th>Date</th>
                            <th class="text-end">Amount</th>
                            <th>Method</th>
                            <th>Reference</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="p in payments" :key="p.id">
                            <td>{{ formatDate(p.payment_date) }}</td>
                            <td class="text-end">
                              {{ formatMoney(p.amount) }}
                            </td>
                            <td>{{ p.method || "—" }}</td>
                            <td>{{ p.reference || "—" }}</td>
                            <td class="text-truncate" style="max-width: 250px">
                              {{ p.notes || "—" }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Signature -->
                <div
                  class="row justify-content-end align-items-end text-end border-bottom mb-3 dq-signature-row"
                >
                  <div class="col-md-3">
                    <div class="text-end">
                      <img
                        src="@/assets/img/sign.svg"
                        class="img-fluid"
                        alt="sign"
                      />
                    </div>
                    <div class="text-end mb-3">
                      <h6 class="fs-14 fw-medium pe-3">
                        {{
                          activeCompany?.contact_person ||
                          "Authorized Signatory"
                        }}
                      </h6>
                      <p>
                        {{ activeCompany?.position || "Finance / Accounts" }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Footer payment info -->
                <div class="text-center dq-payment-footer">
                  <div class="mb-2">
                    <template v-if="companyLogo">
                      <img :src="companyLogo" class="img-fluid" alt="logo" />
                    </template>
                    <template v-else>
                      <div
                        class="avatar avatar-lg bg-primary text-white d-inline-flex align-items-center justify-content-center rounded"
                      >
                        <span class="fw-bold">{{ companyInitials }}</span>
                      </div>
                    </template>
                  </div>

                  <p class="text-dark mb-1">
                    Payment Made Via bank transfer / Cheque in the name of
                    {{ activeCompany?.company_name || "Company Name" }}
                  </p>
                  <div class="d-flex justify-content-center align-items-center">
                    <p class="fs-12 mb-0 me-3">
                      Bank Name :
                      <span class="text-dark">
                        {{ activeCompany?.bank_name || "Bank Name" }}
                      </span>
                    </p>
                    <p class="fs-12 mb-0 me-3">
                      Account Number :
                      <span class="text-dark">
                        {{ activeCompany?.bank_account_no || "0000000000" }}
                      </span>
                    </p>
                    <p class="fs-12">
                      Branch :
                      <span class="text-dark">
                        {{ activeCompany?.bank_branch || "Main Branch" }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- When invoice not yet loaded -->
      <div v-else class="text-center py-5">
        <div class="spinner-border mb-3" role="status"></div>
        <p class="text-muted mb-0">Loading invoice...</p>
      </div>

      <!-- Actions under card -->
      <div
        class="d-flex justify-content-center align-items-center mb-4 dq-invoice-actions"
        v-if="invoice"
      >
        <button
          type="button"
          class="btn btn-primary d-flex justify-content-center align-items-center me-2"
          v-can="'Invoice.Export'"
          @click="printInvoice"
        >
          <i class="ti ti-printer me-2"></i>Print Invoice
        </button>
        <button
          v-if="!isFullyPaid"
          type="button"
          class="btn btn-white d-flex justify-content-center align-items-center border"
          v-can="'Invoice.Create'"
          @click="cloneInvoice"
        >
          <i class="ti ti-copy me-2"></i>Clone Invoice
        </button>
        <button
          v-if="!isFullyPaid && invoice.adjustment_type !== 'credit_note'"
          type="button"
          class="btn btn-white d-flex justify-content-center align-items-center border ms-2"
          v-can="'Invoice.Create'"
          @click="issueCreditNote"
        >
          <i class="ti ti-file-delta me-2"></i>Issue Credit Note
        </button>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
    <!-- /Footer -->
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
.dq-compliance-card,
.dq-log-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 1rem 1.1rem;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
  height: 100%;
}

.dq-compliance-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7a99;
  margin-bottom: 0.35rem;
}

.dq-meta-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7c8799;
  margin-bottom: 0.2rem;
}

.dq-meta-value {
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.dq-log-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 260px;
  overflow: auto;
}

.dq-log-item {
  border-top: 1px solid #eef1f4;
  padding-top: 0.75rem;
}

.dq-log-item:first-child {
  border-top: 0;
  padding-top: 0;
}

@media (max-width: 991.98px) {
  .dq-invoice-shell > .col-sm-10 {
    max-width: 100%;
    flex: 0 0 100%;
  }
}

@media (max-width: 767.98px) {
  .dq-invoice-header,
  .dq-invoice-parties,
  .dq-signature-row {
    row-gap: 1rem;
  }

  .dq-invoice-header .text-end,
  .dq-signature-row .text-end {
    text-align: left !important;
  }

  .dq-invoice-status-col {
    margin-top: 0.5rem;
  }

  .dq-payment-footer .d-flex {
    flex-direction: column;
    align-items: center !important;
    gap: 0.5rem;
  }

  .dq-payment-footer .me-3 {
    margin-right: 0 !important;
  }

  .dq-invoice-actions {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.75rem;
  }

  .dq-invoice-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .dq-invoice-actions .me-2,
  .dq-invoice-actions .ms-2 {
    margin-right: 0 !important;
    margin-left: 0 !important;
  }
}
</style>


